'use client';

import { useState } from 'react';
import { Box, styled } from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import { CircularProgress, Typography } from '@/primitives';
import { BackButton } from '@/components/buttons/BackButton';
import ImageLightbox from '@/components/publicSearch/ImageLightbox';
import AnimalDetailFields from '@/components/publicSearch/animal/AnimalDetailFields';
import type { AnimalDetailData } from '@/components/publicSearch/animal/AnimalDetailFields';
import { getMemberDisplayName } from '@/components/publicSearch/searchSection/helpers';
import { useDocumentImageWithSociety } from '@/hooks/publicSearch/useDocumentImageWithSociety';
import type { useAnimalView } from '@/hooks/publicSearch/animal/useAnimalView';
import type { parseAnimalDetailFieldsConfig } from '@/hooks/publicSearch/animal/animalEbvGrid';

const Hr = styled('hr')(({ theme }) => ({
  border: 'none',
  borderTop: `1px solid ${theme.palette.divider}`,
  margin: '16px 0',
}));

type AnimalView = ReturnType<typeof useAnimalView>;
type AnimalInfoView = Pick<
  AnimalView,
  | 'animalImgUrls'
  | 'animalDisplayName'
  | 'breeder'
  | 'owner'
  | 'isLoading'
  | 'isLoadingImg'
> & { animalData: AnimalDetailData | null | undefined };
type DetailFieldsConfig = ReturnType<typeof parseAnimalDetailFieldsConfig>;

type AnimalInfoCardProps = {
  animalViewData: AnimalInfoView;
  orgId: string;
  detailFieldsConfig: DetailFieldsConfig;
  societyCode: string | undefined;
  /** Hide the back button when viewing within a sale-catalogue lot context. */
  isInSaleCatalogueContext: boolean;
  /** Prefer lot_anim_name (e.g. "Herd Bull: …") when opened from a sale catalogue. */
  animalDisplayNameOverride?: string | null;
  onBack: () => void;
};

/**
 * Animal-detail header card: title bar, the animal photo slot, and the breeder /
 * owner / detail fields. Owns its own image resolution and member-name derivation
 * so the page only hands it the raw animal view data.
 */
export default function AnimalInfoCard({
  animalViewData,
  orgId,
  detailFieldsConfig,
  societyCode,
  isInSaleCatalogueContext,
  animalDisplayNameOverride,
  onBack,
}: AnimalInfoCardProps) {
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
  const primaryImageUrl = animalViewData.animalImgUrls[0] ?? null;
  const { imgSrc: animalImageWithSociety } = useDocumentImageWithSociety(
    primaryImageUrl,
    orgId,
  );
  const displayName =
    animalDisplayNameOverride?.trim() ||
    animalViewData.animalDisplayName ||
    null;

  const breederId = animalViewData.breeder?.additionalInfo?.id;
  const breederDisplayName = getMemberDisplayName(
    animalViewData.breeder?.additionalInfo,
  );
  const ownerId = animalViewData.owner?.additionalInfo?.id;
  const ownerDisplayName = getMemberDisplayName(
    animalViewData.owner?.additionalInfo,
  );

  /** Reserve image column while loading or when a primary image URL may resolve. */
  const showAnimalImageSlot =
    animalViewData.isLoading ||
    animalViewData.isLoadingImg ||
    !!primaryImageUrl ||
    !!animalImageWithSociety;

  return (
    <Box
      sx={{
        mt: '2px',
        backgroundColor: 'background.paper',
        borderRadius: 2,
        px: '16px',
        pt: '8px',
        pb: '16px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {!isInSaleCatalogueContext && (
          <BackButton searchStyle sx={{ flexShrink: 0 }} onClick={onBack} />
        )}
        <PhotoCameraOutlinedIcon
          sx={{ color: 'text.secondary', fontSize: 18 }}
        />
        <Typography sx={{ fontWeight: 600 }}>Animal Information</Typography>
      </Box>
      <Hr sx={{ mt: 2, mb: 2, mx: 0 }} />

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          alignItems: 'stretch',
          gridTemplateColumns: {
            xs: '1fr',
            md: showAnimalImageSlot ? 'minmax(0, 1fr) 160px' : 'minmax(0, 1fr)',
          },
          gridTemplateAreas: {
            xs: showAnimalImageSlot ? '"image"\n"details"' : '"details"',
            md: showAnimalImageSlot ? '"details image"' : '"details"',
          },
        }}
      >
        {/* Details first in DOM: left on md, below image on xs when image slot shown */}
        <Box sx={{ gridArea: 'details', minWidth: 0 }}>
          {animalViewData.isLoading ? (
            <CircularProgress />
          ) : (
            animalViewData.animalData && (
              <Box sx={{ height: '100%' }}>
                <AnimalDetailFields
                  config={detailFieldsConfig}
                  animalData={animalViewData.animalData}
                  animalDisplayName={displayName}
                  breeder={
                    breederId
                      ? {
                          id: breederId,
                          name: breederDisplayName,
                          detailsAvailFlag:
                            animalViewData.breeder?.additionalInfo
                              ?.detailsAvailFlag ?? null,
                        }
                      : undefined
                  }
                  owner={
                    ownerId
                      ? {
                          id: ownerId,
                          name: ownerDisplayName,
                          detailsAvailFlag:
                            animalViewData.owner?.additionalInfo
                              ?.detailsAvailFlag ?? null,
                        }
                      : undefined
                  }
                  orgCode={societyCode}
                />
              </Box>
            )
          )}
        </Box>

        {showAnimalImageSlot ? (
          <Box sx={{ gridArea: 'image', minWidth: 0 }}>
            {(() => {
              if (animalViewData.isLoading || animalViewData.isLoadingImg) {
                return (
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: { xs: 'auto', md: '1/1' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <CircularProgress size={40} />
                  </Box>
                );
              }
              if (animalImageWithSociety) {
                return (
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setIsLightboxOpen(true)}
                    aria-label="View full-size image"
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.paper',
                      overflow: 'hidden',
                      padding: 0,
                      width: '100%',
                      cursor: 'pointer',
                      display: 'block',
                      transition: (theme) =>
                        theme.transitions.create(
                          ['border-color', 'box-shadow', 'transform'],
                          { duration: theme.transitions.duration.shorter },
                        ),
                      '&:hover': {
                        boxShadow: 1,
                        transform: 'translateY(-2px)',
                      },
                      '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      alt={displayName ?? 'Animal image'}
                      src={animalImageWithSociety}
                      sx={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        backgroundColor: 'background.default',
                      }}
                    />
                  </Box>
                );
              }
              if (primaryImageUrl) {
                return (
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: { xs: 'auto', md: '1/1' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <CircularProgress size={40} />
                  </Box>
                );
              }
              return null;
            })()}
          </Box>
        ) : null}
      </Box>

      <ImageLightbox
        open={isLightboxOpen}
        src={animalImageWithSociety}
        alt={displayName ?? 'Animal image'}
        title={displayName ?? undefined}
        onClose={() => setIsLightboxOpen(false)}
      />
    </Box>
  );
}
